import React, { useCallback, useEffect, useMemo, useState } from 'react'

import withMainLayout from '../../hocs/withMainLayout'

function fmtDate(d) {
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

function defaultRange() {
  const to = new Date()
  const from = new Date()
  from.setDate(from.getDate() - 29)
  return { from: fmtDate(from), to: fmtDate(to) }
}

function fmtNumber(n) {
  if (n === null || n === undefined) return '—'
  return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
}

function Card({ label, platform, value, sub }) {
  const platformColor = platform === 'ios' ? '#1f2330' : '#3ddc84'
  return (
    <div style={styles.card}>
      <div style={styles.label}>
        <span
          style={{ ...styles.platformDot, background: platformColor }}
        />
        {label}
      </div>
      <div style={{ ...styles.number, color: value === null ? '#d1d5db' : '#1f2330' }}>
        {fmtNumber(value)}
      </div>
      <div style={{ ...styles.sub, color: value === null ? '#ef4444' : '#6b7280' }}>
        {value === null ? 'недоступно' : sub}
      </div>
    </div>
  )
}

const Downloads = () => {
  const [{ from, to }, setRange] = useState(defaultRange)
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)

  const fetchData = useCallback(async (f, t) => {
    setLoading(true)
    setError(null)
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(
        `/api/v2/analytics/downloads?from=${f}&to=${t}`,
        {
          headers: { Authorization: token ? `Bearer ${token}` : '' }
        }
      )
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.error || `HTTP ${res.status}`)
      }
      const json = await res.json()
      setData(json)
    } catch (e) {
      setError(e.message)
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData(from, to)
  }, [fetchData, from, to])

  const setPreset = useCallback(days => {
    const t = new Date()
    const f = new Date()
    f.setDate(f.getDate() - (days - 1))
    setRange({ from: fmtDate(f), to: fmtDate(t) })
  }, [])

  const presetActive = useMemo(() => {
    const t = new Date(to)
    const f = new Date(from)
    const today = fmtDate(new Date())
    if (to !== today) return null
    const days = Math.round((t - f) / 86400000) + 1
    return days
  }, [from, to])

  const ios = data && data.ios
  const android = data && data.android
  const warnings = (data && data.warnings) || []

  return (
    <div style={styles.page}>
      <div style={styles.head}>
        <h1 style={styles.h1}>Скачивания</h1>
        <div style={styles.crumbs}>Аналитика · Скачивания</div>
      </div>

      <div style={styles.filters}>
        <label style={styles.label2}>
          <span>С</span>
          <input
            type="date"
            value={from}
            max={to}
            onChange={e => setRange(r => ({ ...r, from: e.target.value }))}
            style={styles.dateInput}
          />
        </label>
        <label style={styles.label2}>
          <span>По</span>
          <input
            type="date"
            value={to}
            min={from}
            onChange={e => setRange(r => ({ ...r, to: e.target.value }))}
            style={styles.dateInput}
          />
        </label>
        <div style={styles.presets}>
          {[7, 30, 90, 365].map(days => (
            <button
              key={days}
              onClick={() => setPreset(days)}
              style={{
                ...styles.presetBtn,
                ...(presetActive === days ? styles.presetBtnActive : {})
              }}
            >
              {days === 365 ? '1 год' : `${days} дней`}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div style={styles.errorBanner}>
          Ошибка запроса: <code>{error}</code>
        </div>
      )}

      {warnings.map((w, i) => (
        <div key={i} style={styles.warning}>
          ⚠️ {w}
        </div>
      ))}

      <div style={styles.grid}>
        <Card
          label="iOS · всего"
          platform="ios"
          value={loading ? null : ios && ios.total}
          sub={`${from} — ${to}`}
        />
        <Card
          label="iOS · первичные"
          platform="ios"
          value={loading ? null : ios && ios.firstTime}
          sub={
            ios && ios.total
              ? `${Math.round((ios.firstTime / ios.total) * 100)}% от всех`
              : ''
          }
        />
        <Card
          label="Android · всего"
          platform="android"
          value={loading ? null : android && android.total}
          sub={`${from} — ${to}`}
        />
        <Card
          label="Android · первичные"
          platform="android"
          value={loading ? null : android && android.firstTime}
          sub={
            android && android.total
              ? `${Math.round((android.firstTime / android.total) * 100)}% от всех`
              : ''
          }
        />
      </div>

      <div style={styles.footer}>
        <span>
          Последняя сверка: iOS —{' '}
          <b>{(ios && ios.lastSyncedAt) || '—'}</b> · Android —{' '}
          <b>{(android && android.lastSyncedAt) || '—'}</b>
        </span>
        <span>Данные публикуются сторами с задержкой 1–2 дня</span>
      </div>

      {loading && <div style={styles.loading}>Загружаем из App Store / Play…</div>}
    </div>
  )
}

const styles = {
  page: { padding: '24px 32px', maxWidth: 1100, fontFamily: 'Roboto, sans-serif' },
  head: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    marginBottom: 20
  },
  h1: { margin: 0, fontSize: 24, fontWeight: 700 },
  crumbs: { color: '#9ca3af', fontSize: 13 },
  filters: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: 16,
    display: 'flex',
    gap: 18,
    alignItems: 'center',
    marginBottom: 18
  },
  label2: {
    fontSize: 12,
    color: '#6b7280',
    display: 'flex',
    flexDirection: 'column',
    gap: 4
  },
  dateInput: {
    fontSize: 14,
    padding: '8px 10px',
    border: '1px solid #d1d5db',
    borderRadius: 8,
    background: '#fff',
    color: '#1f2330'
  },
  presets: { marginLeft: 'auto', display: 'flex', gap: 6 },
  presetBtn: {
    fontSize: 12,
    padding: '6px 10px',
    border: '1px solid #d1d5db',
    background: '#fff',
    borderRadius: 6,
    cursor: 'pointer',
    color: '#4b5563'
  },
  presetBtnActive: {
    background: '#4f46e5',
    borderColor: '#4f46e5',
    color: '#fff'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 16,
    marginBottom: 12
  },
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: '16px 18px'
  },
  label: {
    fontSize: 11,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    display: 'flex',
    alignItems: 'center',
    gap: 6
  },
  platformDot: {
    width: 12,
    height: 12,
    borderRadius: 3,
    display: 'inline-block'
  },
  number: {
    fontSize: 30,
    fontWeight: 700,
    marginTop: 8,
    letterSpacing: '-0.5px',
    fontVariantNumeric: 'tabular-nums'
  },
  sub: { fontSize: 12, marginTop: 4 },
  footer: {
    marginTop: 16,
    paddingTop: 14,
    borderTop: '1px solid #e5e7eb',
    fontSize: 12,
    color: '#6b7280',
    display: 'flex',
    justifyContent: 'space-between'
  },
  errorBanner: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#991b1b',
    fontSize: 13,
    borderRadius: 10,
    padding: '10px 14px',
    marginBottom: 12
  },
  warning: {
    background: '#fffbeb',
    border: '1px solid #fde68a',
    color: '#92400e',
    fontSize: 13,
    borderRadius: 10,
    padding: '10px 14px',
    marginBottom: 12
  },
  loading: {
    marginTop: 12,
    fontSize: 13,
    color: '#6b7280'
  }
}

export default withMainLayout(Downloads)
